/**
 * ออปชันสุ่มของอุปกรณ์ (PLAN ขั้นที่ 3.6)
 *
 * - ตารางออปชันอยู่ใน `data/options.json` (กฎข้อ 4) ผลใช้ฟิลด์เดิมของ `CardBonus`
 *   จึงรวมเข้าค่ารองผ่าน `cardTotals` ที่เดียวกับการ์ด ไม่มีระบบคู่ขนาน
 * - จำนวนบรรทัดตามความหายาก (`OPTION_LINES`) ช่วงค่าโตตามชั้นเลเวลของของ (`optionTier`)
 * - **ทอยที่ server เท่านั้น** (`server/src/combat.ts` ส่ง rng เข้ามา) ฟังก์ชันที่นี่ไม่สุ่มเอง
 *   ของจากร้าน NPC ไม่มีออปชัน — ได้จากดรอปเท่านั้น
 * - เก็บเป็นสตริงย่อ `id:ค่า;id:ค่า` ทั้งในชิ้นมีตัวตน (ช่องที่สี่ของ gear) และช่องที่สวม
 */
import optionsJson from "./data/options.json" with { type: "json" };
import { CLASSES } from "./data.js";
import { describeEndow, endowOf } from "./endow.js";
import { stoneForSlot, REFINE_STONE_ID } from "./refine.js";
import { ELEMENT_NAMES, RACE_NAMES, } from "./types.js";
export const OPTION_LIST = optionsJson;
export const OPTIONS = Object.freeze(Object.fromEntries(OPTION_LIST.map((o) => [o.id, o])));
/** จำนวนบรรทัดออปชันตามความหายาก */
export const OPTION_LINES = {
    common: 0, fine: 1, rare: 2, epic: 3, legend: 4,
};
/** ชั้นของช่วงค่า จากเลเวลที่ใส่ของได้ (0: <20 · 1: <40 · 2: <60 · 3: 60+) */
export function optionTier(def) {
    const lv = def?.reqLevel ?? 1;
    return lv < 20 ? 0 : lv < 40 ? 1 : lv < 60 ? 2 : 3;
}
/** ค่าเปอร์เซ็นต์ (เศษส่วน) ปัดสามตำแหน่ง ค่าคงที่ปัดเป็นจำนวนเต็ม */
function isFraction(o) {
    return o.effect.kind === "race" || o.effect.kind === "res"
        || (o.effect.kind === "mod" && o.effect.key.endsWith("Mul"));
}
function roundValue(o, v) {
    return isFraction(o) ? Math.round(v * 1000) / 1000 : Math.round(v);
}
/** ออปชันที่ของชิ้นนี้ออกได้ */
export function optionPool(def, classId) {
    const slot = def?.kind === "equip" ? def.slot : undefined;
    if (!slot)
        return [];
    return OPTION_LIST.filter((o) => o.slots.includes(slot) && (!o.classes?.length || !classId || o.classes.includes(classId)));
}
/**
 * ทอยออปชันให้ของชิ้นหนึ่ง **rng ต้องมาจาก server** (คืนค่า 0..1)
 * ไม่ซ้ำ id ในชิ้นเดียว · ค่าอยู่ในช่วงของชั้นนั้นเสมอ
 */
export function rollOptionsWith(def, rng) {
    const lines = OPTION_LINES[def?.rarity ?? "common"] ?? 0;
    let pool = optionPool(def);
    const tier = optionTier(def);
    const out = [];
    while (out.length < lines && pool.length > 0) {
        const total = pool.reduce((s, o) => s + o.weight, 0);
        let pick = rng() * total;
        let chosen = pool[pool.length - 1];
        for (const o of pool) {
            pick -= o.weight;
            if (pick < 0) {
                chosen = o;
                break;
            }
        }
        const [lo, hi] = rangeOf(chosen, tier);
        out.push({ id: chosen.id, v: roundValue(chosen, lo + (hi - lo) * rng()) });
        pool = pool.filter((o) => o.id !== chosen.id);
    }
    return out;
}
/** ช่วงของออปชันในชั้นนั้น (ชั้นเกินตารางใช้ชั้นสุดท้าย) */
export function rangeOf(o, tier) {
    return o.ranges[Math.min(tier, o.ranges.length - 1)] ?? [0, 0];
}
export function encodeOptions(list) {
    return list.map((o) => `${o.id}:${o.v}`).join(";");
}
/**
 * อ่านสตริงย่อ ตัด id ที่ไม่มีแล้วกับค่าที่ไม่ใช่ตัวเลข และหนีบค่าไม่เกินค่าสูงสุดของตาราง
 * (กันข้อมูลเก่าหลังปรับสมดุล หรือข้อมูลปลอมแรงเกินเพดาน)
 */
export function parseOptions(raw) {
    const out = [];
    for (const part of (raw ?? "").split(";")) {
        const [id = "", val = ""] = part.split(":");
        // บรรทัดธาตุอาวุธ (endow.ts) ไม่อยู่ในตาราง ค่าเป็น 1 เสมอ มีได้บรรทัดเดียว
        if (endowOf(id)) {
            if (!out.some((x) => endowOf(x.id)))
                out.push({ id, v: 1 });
            continue;
        }
        const o = OPTIONS[id];
        const v = Number(val);
        if (!o || !Number.isFinite(v) || v <= 0 || out.some((x) => x.id === id))
            continue;
        const max = Math.max(...o.ranges.map((r) => r[1]));
        out.push({ id, v: Math.min(v, max) });
    }
    return out;
}
/** แปลงออปชันเป็นผลแบบเดียวกับการ์ด รวมด้วย `cardTotals` */
export function optionBonus(list) {
    const b = {};
    for (const r of list) {
        const o = OPTIONS[r.id];
        if (!o)
            continue;
        const e = o.effect;
        if (e.kind === "stat")
            (b.stats ??= {})[e.key] = (b.stats?.[e.key] ?? 0) + r.v;
        else if (e.kind === "race")
            (b.raceDmg ??= {})[e.key] = (b.raceDmg?.[e.key] ?? 0) + r.v;
        else if (e.kind === "res")
            (b.elementRes ??= {})[e.key] = (b.elementRes?.[e.key] ?? 0) + r.v;
        else if (e.key.endsWith("Mul")) {
            const k = e.key;
            b[k] = (b[k] ?? 1) * (1 + r.v);
        }
        else {
            const k = e.key;
            b[k] = (b[k] ?? 0) + r.v;
        }
    }
    return b;
}
/** ข้อความหนึ่งบรรทัด เช่น "ATK +4%" "STR +3" "ดาเมจใส่แมลง +6%" */
export function describeOption(r) {
    const el = endowOf(r.id);
    if (el)
        return describeEndow(el);
    const o = OPTIONS[r.id];
    if (!o)
        return r.id;
    const val = isFraction(o) ? `${Math.round(r.v * 1000) / 10}%` : String(r.v);
    if (o.effect.kind === "race")
        return `ดาเมจใส่${RACE_NAMES[o.effect.key]} +${val}`;
    if (o.effect.kind === "res")
        return `ต้านธาตุ${ELEMENT_NAMES[o.effect.key]} ${val}`;
    return `${o.name} +${val}`;
}
/** คุณภาพการทอย 0..1 (ค่าเทียบช่วงของชั้น) ใช้เลือกสีตอนแสดง */
export function optionQuality(r, def) {
    if (endowOf(r.id))
        return 1;
    const o = OPTIONS[r.id];
    if (!o)
        return 0;
    const [lo, hi] = rangeOf(o, optionTier(def));
    return hi > lo ? Math.max(0, Math.min(1, (r.v - lo) / (hi - lo))) : 1;
}
/** ใช้ใน describe ของอาชีพ (ออปชันเจาะจงอาชีพในอนาคต) */
export function optionClassNames(o) {
    return (o.classes ?? []).map((c) => CLASSES[c]?.name ?? c).join("/");
}
/* ---------- ทอยซ้ำทีละบรรทัดที่โรงตีเหล็ก (ข้อ 4 ของขั้น 3.6) ---------- */
/** โกลด์ต่อการทอยซ้ำหนึ่งบรรทัด ตามชั้นของของ — **ห้ามรับเพชร** (กฎข้อ 2) */
export const OPTION_REROLL_GOLD = [300, 800, 2000, 5000];
export function rerollCost(def, slot) {
    const tier = optionTier(def);
    return {
        gold: OPTION_REROLL_GOLD[Math.min(tier, OPTION_REROLL_GOLD.length - 1)] ?? 0,
        stone: stoneForSlot(slot) ?? REFINE_STONE_ID,
    };
}
/**
 * ออปชันที่บรรทัด `line` เปลี่ยนเป็นได้ พร้อมโอกาส (แสดงให้ผู้เล่นเห็นก่อนกด — ทุกการสุ่มต้องบอกอัตรา)
 * ไม่ออกซ้ำกับบรรทัดอื่นในชิ้นเดียวกัน (ทอยแล้วได้แบบเดิมของบรรทัดนี้ได้)
 */
export function rerollChances(def, current, line) {
    // บรรทัดธาตุอาวุธทอยไม่ได้ (เปลี่ยนธาตุต้องร่ายใหม่ที่โรงตีเหล็ก)
    if (current[line] && endowOf(current[line].id))
        return [];
    const others = new Set(current.filter((_, i) => i !== line).map((o) => o.id));
    const pool = optionPool(def).filter((o) => !others.has(o.id));
    const total = pool.reduce((s, o) => s + o.weight, 0);
    return total > 0 ? pool.map((o) => ({ id: o.id, chance: o.weight / total })) : [];
}
/** ทอยบรรทัดเดียวใหม่ (rng จาก server) คืนรายการใหม่ทั้งชุด บรรทัดอื่นไม่เปลี่ยน */
export function rerollLineWith(def, current, line, rng) {
    if (line < 0 || line >= current.length)
        return undefined;
    const chances = rerollChances(def, current, line);
    if (chances.length === 0)
        return undefined;
    let pick = rng();
    let id = chances[chances.length - 1].id;
    for (const c of chances) {
        pick -= c.chance;
        if (pick < 0) {
            id = c.id;
            break;
        }
    }
    const o = OPTIONS[id];
    const [lo, hi] = rangeOf(o, optionTier(def));
    const next = [...current];
    next[line] = { id, v: roundValue(o, lo + (hi - lo) * rng()) };
    return next;
}
//# sourceMappingURL=options.js.map